import { OBLIGATION_AREA_TEXT, PHASE_STEP, type AiuterebbeDto, type GradoConclusione, type ObbligoDto, type ObligationArea, type PhaseNumber, type PhaseSixDto, type ScartoRigaDto } from '@bk/contracts';
import { useState, type ReactNode } from 'react';
import { navigate } from '../../lib/router';
import { useTenant } from '../../lib/useTenant';
import { talkAbout } from './Fase';
import { PhaseFrame, usePhase } from './fasi-comune';
import type { PhasePageProps } from './fasi-pagine';

/**
 * La fase 6 «Gli obblighi di legge» (docs/mockup/percorso-sinapsi/09-fase6; contratto n. 1 versione 2.4, art. 8, fase 6;
 * ADR 0177): la testa con il livello, «Come l'ho preparata», i bloccanti, i numeri, il registro per area con i conti e la
 * nota sui consulenti esterni, ogni obbligo per intero con il «perché?», i «non si applica» con il perché, lo scarto, le
 * cose che aiuterebbero, le domande, «Parliamone», «Non va bene» e «Confermo la fase». I pezzi comuni sono quelli
 * dell'ossatura (`Fase.tsx`, ADR 0174). La pagina non mostra i rimandi al contratto (art. 4.1).
 *
 * Registrata in `fasi-pagine.tsx` per il passo «obblighi»; i dati vengono da `GET /onboarding/fase/6`.
 */

/** La pagina della fase 6, registrata in `fasi-pagine.tsx` per il passo «obblighi». */
export function Fase6Page({ next, onNext, sealing, stopped = false }: PhasePageProps) {
  const { q, reload, confirm, redo } = usePhase<PhaseSixDto>(6);
  return (
    <PhaseFrame
      d={q.data}
      error={q.error}
      eyebrow="Fase 6 · Gli obblighi"
      title={(d) => `Tutto ciò che la legge chiede a ${d.azienda ?? 'la tua azienda'}, una volta sola`}
      working="Preparo gli obblighi"
      questionsIntro={QUESTIONS_INTRO6}
      scrapIntro=""
      next={next}
      onNext={onNext}
      sealing={sealing}
      stopped={stopped}
      onConfirm={confirm}
      onRedo={redo}
      reload={reload}
      beforeQuestions={(d) => (
        <>
          <ScrapTable list={d.scarto_fase} intro="Per ogni obbligo che si applica: se le fonti mostrano che l’azienda di oggi ha ciò che lo realizza. Non dice se l’obbligo è adempiuto: lo dirà Timonio al lavoro (contratto n. 2)." />
          <HelpRows list={d.aiuterebbero} />
        </>
      )}
    >
      {(d) => <Fase6Body d={d} onTalk={talkAbout} />}
    </PhaseFrame>
  );
}

export const QUESTIONS_INTRO6 = 'Solo i fatti che decidono un obbligo, nell’ordine del peso della sanzione. Non ti chiedo se un obbligo è già fatto: questo incarico non lo giudica.';

const gradeChip = (g: GradoConclusione) => <span className={`sn-chip ${g === 'certo' ? 'T' : g === 'probabile' ? 'C' : 'plain'}`}>{g === 'certo' ? 'Certo' : g === 'probabile' ? 'Probabile' : 'Possibile'}</span>;

/** Lo stato dello scarto come la pagina di prova: «c'è», «non c'è» (una fonte lo dice), «le fonti non lo mostrano». */
export function ScrapState({ x }: { x: ScartoRigaDto }) {
  if (x.oggi === 'nessuno') return <span className="sn-src ok">Nessuno scarto</span>;
  if (x.oggi === 'ha') return <span className="sn-src ok">C’è</span>;
  if (x.oggi === 'coperta') return <span className="sn-src ok">Coperta da altri</span>;
  return <span className="sn-src gold">{x.fonte ? 'Non c’è' : 'Le fonti non lo mostrano'}</span>;
}

/** Lo scarto della fase, con «da dove lo so» (art. 8, «Lo scarto nasce dove nasce la voce»). */
export function ScrapTable({ list, intro }: { list: ScartoRigaDto[]; intro: string }) {
  if (!list.length) return null;
  return (
    <section className="sn-panel gold" id="scarto" style={{ gap: 10, padding: '20px 24px' }}>
      <h2 className="sn-h2">Lo scarto di questa fase</h2>
      <p className="sn-note" style={{ fontSize: 14 }}>
        {intro}
      </p>
      <table className="sn-tbl wide">
        <thead>
          <tr>
            <th>Voce dell’azienda al suo meglio</th>
            <th style={{ width: 190 }}>L’azienda di oggi</th>
            <th>Da dove lo so</th>
          </tr>
        </thead>
        <tbody>
          {list.map((x, k) => (
            <tr key={k}>
              <td>{x.oggi === 'manca' ? <b>{x.voce}</b> : x.voce}</td>
              <td>
                <ScrapState x={x} />
              </td>
              <td className="f on">
                {x.fonte ?? <span className="sn-mute">{x.oggi === 'nessuno' ? '—' : 'nessuna fonte collegata'}</span>}
                {x.perche && <span className="sub"> {x.perche}</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="sn-note">Lo scarto dice cosa le fonti mostrano o non mostrano oggi, non se fate bene o male: la diagnosi (fase 10) lo raccoglie per reparto, le proposte (fase 11) lo prezzano.</p>
    </section>
  );
}

/** Le cose che aiuterebbero qui (art. 7-bis.13): voci del registro di fasi già fatte, con il collegamento. */
export function HelpRows({ list }: { list: AiuterebbeDto[] }) {
  const { tenant } = useTenant();
  if (!list.length) return null;
  const step = (fase: number) => PHASE_STEP[fase as PhaseNumber] ?? (fase === 0 ? 'visura' : null);
  const state = (s: string) => (s === 'aperta' ? 'Aperta' : s === 'risposta' ? 'Risposta tua' : 'Scelta di Timonio');
  return (
    <section className="sn-panel" style={{ gap: 10, padding: '20px 24px' }}>
      <h2 className="sn-h2">Cose che aiuterebbero qui</h2>
      <p className="sn-note" style={{ fontSize: 14 }}>
        Non sono domande nuove: sono voci del registro di fasi già fatte. Se rispondi, questa fase si aggiorna da sola, senza rifarla.
      </p>
      <table className="sn-tbl wide">
        <tbody>
          {list.map((x) => (
            <tr key={x.id}>
              <td>
                <b>{x.voce}</b>
              </td>
              <td className="v mute" style={{ width: 70, fontFamily: 'var(--sn-mono)' }}>
                fase {x.fase}
              </td>
              <td style={{ width: 150 }}>
                <span className="sn-src gold">{state(String(x.stato))}</span>
              </td>
              <td className="sn-note">{x.cosa_cambierebbe}</td>
              <td style={{ width: 80 }}>{step(x.fase) && <button type="button" className="sn-link" onClick={() => navigate(`/t/${tenant.id}/configurazione/${step(x.fase)}`)}>Rispondi</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/** Una riga «chiave · valore» di un obbligo per intero. */
const Kv = ({ k, children }: { k: string; children: ReactNode }) => (
  <>
    <dt>{k}</dt>
    <dd>{children}</dd>
  </>
);

/** Un obbligo per intero, come lo apre il registro: ogni campo, il «perché?», «Correggi». */
export function ObligationCard({ o, onTalk }: { o: ObbligoDto; onTalk?: (text: string) => void }) {
  const [why, setWhy] = useState(false);
  return (
    <article className="sn-panel" style={{ gap: 10, padding: '18px 22px' }}>
      <div className="sn-ph" style={{ flexWrap: 'wrap', gap: 8 }}>
        <h3 className="sn-h2" style={{ fontSize: 16 }}>
          {o.nome}
        </h3>
        <span className="sn-chips">
          {gradeChip(o.grado)}
          <span className="sn-chip plain">{o.etichetta}</span>
          <span className="lbl">{OBLIGATION_AREA_TEXT[o.area]}</span>
        </span>
      </div>
      <dl className="sn-dl wide">
        <Kv k="Norma vigente">
          <b>{o.norma.testo}</b>
          {o.norma.citazione && <span className="sub"> «{o.norma.citazione}»</span>}
          {o.norma.indirizzo_web && (
            <>
              {' '}
              <a href={o.norma.indirizzo_web} target="_blank" rel="noreferrer" style={{ fontSize: 13 }}>
                il testo intero dell’articolo
              </a>
            </>
          )}
          {o.norma.prassi && <span className="sub"> Come la applica l’ente: {o.norma.prassi}</span>}
        </Kv>
        <Kv k={o.conclusione === 'si_applica' ? 'Fatto che lo fa scattare' : 'Perché non si applica'}>
          <span className={o.da_confermare ? 'sn-gold' : undefined}>{o.fatto}</span>
        </Kv>
        {o.cosa_chiede_testo && <Kv k="Cosa chiede">{o.cosa_chiede_testo}</Kv>}
        {(o.cadenza || o.scadenza) && <Kv k="Cadenza e scadenza">{[o.cadenza, o.scadenza].filter(Boolean).join('; ')}</Kv>}
        {o.ente && <Kv k="Ente">{o.ente}</Kv>}
        {o.piattaforma && <Kv k="Piattaforma pubblica">{o.piattaforma}</Kv>}
        {o.sanzione && <Kv k="Sanzione">{o.sanzione}</Kv>}
        {o.conclusione === 'si_applica' && (
          <Kv k="Responsabile">
            <span className="sn-dash">Da assegnare</span>
            {o.responsabile && <span className="sn-mute"> · {o.responsabile}</span>}
          </Kv>
        )}
        {o.realizzato_da.length > 0 && (
          <Kv k="Lo realizza: fase e tipo">
            {o.realizzato_da.map((x) => `fase ${x.fase} · ${x.voce ? `${x.tipo} «${x.voce}»` : x.tipo}`).join('; ')}
            {o.realizzato_da.some((x) => !x.voce && x.fase > 5) && <span className="sn-mute"> (le voci precise le agganciano quelle fasi)</span>}
          </Kv>
        )}
        {o.scarto && (
          <Kv k="Scarto">
            <ScrapState x={o.scarto} /> {o.scarto.perche ?? ''} {o.scarto.fonte && <span className="sn-src">({o.scarto.fonte})</span>}
          </Kv>
        )}
        {o.da_confermare && o.conclusione === 'si_applica' && (
          <Kv k="Nei conti">
            <span className="sn-mute">si mostra, ma non si conta tra quelli che si applicano finché il fatto non arriva</span>
          </Kv>
        )}
      </dl>
      {why && <div className="sn-why">{o.perche}</div>}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <button type="button" className="sn-link" onClick={() => setWhy((w) => !w)}>
          Perché? Il fatto, la norma e il grado
        </button>
        {onTalk && (
          <button type="button" className="sn-link" onClick={() => onTalk(`Sull’obbligo «${o.nome}»: `)}>
            Correggi
          </button>
        )}
      </div>
    </article>
  );
}

/** Quali obblighi aprire per intero quando il titolare non ha scelto un'area: prima quelli che chiedono attenzione. */
function highlighted(list: ObbligoDto[]): ObbligoDto[] {
  const applies = list.filter((o) => o.conclusione === 'si_applica');
  const rank = (o: ObbligoDto) => (o.grado === 'certo' ? 0 : o.grado === 'probabile' ? 1 : 2) + (o.scarto && o.scarto.oggi === 'manca' ? -0.5 : 0);
  return [...applies].sort((a, b) => rank(a) - rank(b)).slice(0, 6);
}

/** Il corpo della pagina della fase 6, sotto la testa, i bloccanti e «cosa è cambiato» della cornice comune; lo scarto e le cose che aiuterebbero vanno prima delle domande. */
export function Fase6Body({ d, onTalk }: { d: PhaseSixDto; onTalk?: (text: string) => void }) {
  const [area, setArea] = useState<ObligationArea | null>(null);
  const [allNo, setAllNo] = useState(false);
  const no = d.obblighi.filter((o) => o.conclusione === 'non_si_applica');
  const unverified = d.obblighi.filter((o) => !o.norma.verificata);
  const shown = area ? d.obblighi.filter((o) => o.area === area && o.conclusione === 'si_applica') : highlighted(d.obblighi.filter((o) => o.norma.verificata));
  return (
    <>
      <section className="sn-figs">
        <div className="sn-fig">
          <p className="k">Si applicano</p>
          <p className="v">{d.conti.si_applicano}</p>
          <p className="s">
            {d.conti.certi} certi · {d.conti.probabili} probabili
          </p>
        </div>
        <div className="sn-fig">
          <p className="k">Non si applicano</p>
          <p className="v">{d.conti.non_si_applicano}</p>
          <p className="s mute">Con il perché</p>
        </div>
        <div className="sn-fig gold">
          <p className="k">Possibili</p>
          <p className="v">{d.conti.possibili}</p>
          <p className="s gold">Un fatto è da confermare</p>
        </div>
        <div className={`sn-fig ${d.dipendenti.da_confermare ? 'gold' : ''}`}>
          <p className="k">Dipendenti</p>
          <p className="v">{d.dipendenti.valore}</p>
          <p className={`s ${d.dipendenti.da_confermare ? 'gold' : 'ok'}`}>{d.dipendenti.fonte}</p>
        </div>
      </section>

      <section className="sn-panel flush" style={{ gap: 0 }}>
        <div className="sn-ph">
          <h2 className="sn-h2">Il registro degli obblighi, per area</h2>
        </div>
        <table className="sn-tbl wide">
          <thead>
            <tr>
              <th>Area degli obblighi</th>
              <th style={{ width: 110 }}>Si applicano</th>
              <th style={{ width: 130 }}>Non si applicano</th>
              <th style={{ width: 100 }}>Possibili</th>
            </tr>
          </thead>
          <tbody>
            {d.aree.map((a) => (
              <tr key={a.area}>
                <td>
                  <button type="button" className="sn-link" aria-pressed={area === a.area} onClick={() => setArea(area === a.area ? null : a.area)} style={{ fontWeight: area === a.area ? 700 : undefined }}>
                    {a.testo}
                  </button>
                </td>
                <td className="v" style={{ fontFamily: 'var(--sn-mono)', fontWeight: 700 }}>
                  {a.si_applicano}
                </td>
                <td className="v mute" style={{ fontFamily: 'var(--sn-mono)' }}>
                  {a.non_si_applicano}
                </td>
                <td className="v gold" style={{ fontFamily: 'var(--sn-mono)' }}>
                  {a.possibili}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="sn-note" style={{ padding: '12px 24px' }}>
          {d.nota_consulenti}
        </p>
      </section>

      <div className="sn-ph">
        <h2 className="sn-h2">{area ? `${OBLIGATION_AREA_TEXT[area]}, per intero` : 'Alcuni obblighi, per intero'}</h2>
        <span className="sn-note">{area ? <button type="button" className="sn-link" onClick={() => setArea(null)}>Torna agli obblighi in evidenza</button> : 'Ogni obbligo del registro si apre così: tocca un’area per vederli tutti.'}</span>
      </div>
      {shown.map((o) => (
        <ObligationCard key={o.id} o={o} onTalk={onTalk} />
      ))}

      {no.length > 0 && (
        <section className="sn-panel flush" style={{ gap: 0 }}>
          <div className="sn-ph">
            <h2 className="sn-h2">Non si applicano · {no.length}</h2>
          </div>
          <table className="sn-tbl wide">
            <thead>
              <tr>
                <th>Obbligo</th>
                <th>Perché non si applica</th>
                <th>Norma</th>
              </tr>
            </thead>
            <tbody>
              {(allNo ? no : no.slice(0, 5)).map((o) => (
                <tr key={o.id}>
                  <td>
                    <b>{o.nome}</b>
                  </td>
                  <td>{o.fatto}</td>
                  <td className="f on">
                    <span className="sn-src">{o.norma.testo}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {no.length > 5 && !allNo && (
            <p style={{ padding: '10px 24px' }}>
              <button type="button" className="sn-link" onClick={() => setAllNo(true)}>
                Vedi gli altri {no.length - 5}
              </button>
            </p>
          )}
        </section>
      )}

      {unverified.length > 0 && (
        <div className="sn-why gold" style={{ fontSize: 13 }}>
          <b>Senza fonte verificata · {unverified.length}.</b> {unverified.map((o) => o.nome).join('; ')}: la norma non si è trovata nel testo vigente, quindi non la cito e non conto l’obbligo finché non è verificata.
        </div>
      )}
    </>
  );
}
